import * as React from 'react';
import { EmployeeAvailability } from 'domain/EmployeeAvailability';
import { useTranslation } from 'react-i18next';
import { Split, SplitItem, Button, Level, LevelItem, Text, ButtonVariant } from '@patternfly/react-core';
import { EditIcon, TrashIcon, OkIcon, WarningTriangleIcon, ErrorCircleOIcon } from '@patternfly/react-icons';
import moment from 'moment';
/* eslint-disable */

export interface AvailabilityEventProps {
  availability: EmployeeAvailability;
  onEdit: (ea: EmployeeAvailability) => void;
  onDelete: (ea: EmployeeAvailability) => void;
  updateEmployeeAvailability: (ea: EmployeeAvailability) => void;
  removeEmployeeAvailability: (ea: EmployeeAvailability) => void;
}

const AvailabilityPopoverHeader: React.FC<AvailabilityEventProps> = (props) => (
  <span>
    <Text>
      {
        `${props.availability.employee.name}, ${
          moment(props.availability.startDateTime).format('LT')}-${
          moment(props.availability.endDateTime).format('LT')}`
      }
    </Text>
    <Button
      aria-label="Edit"
      onClick={() => props.onEdit(props.availability)}
      variant={ButtonVariant.link}
    >
      <EditIcon />
    </Button>
    <Button
      aria-label="Delete"
      onClick={() => props.onDelete(props.availability)}
      variant={ButtonVariant.link}
    >
      <TrashIcon />
    </Button>
  </span>
);

const AvailabilityPopoverBody: React.FC<{ availability: EmployeeAvailability }> = ({ availability }) => {
  const { t } = useTranslation();

  return (
    <>
      <Text>
        <b>Estado:</b> {t(`EmployeeAvailabilityState.${availability.state}`)}
      </Text>

      {availability.reason && (
        <Text>
          <b>Motivo:</b> {t(`AvailabilityReason.${availability.reason}`)}
        </Text>
      )}

      {availability.observation && (
        <Text style={{ whiteSpace: 'pre-wrap', maxWidth: '300px' }}>
          <b>Observación:</b> {availability.observation}
        </Text>
      )}
    </>
  );
};

const AvailabilityEvent: React.FC<AvailabilityEventProps> = (props: AvailabilityEventProps) => {
  const { t } = useTranslation();
  return (
    <span className="availability-event">
      <Level style={{ marginBottom: '4px' }}>
        <LevelItem >
          <div>
            <div
              style={{
                fontWeight: 600,
                lineHeight: 1.1,
              }}
            >
              {t(`EmployeeAvailabilityState.${props.availability.state}`)}
            </div>

            {props.availability.reason && (
              <div
                style={{
                  fontSize: '15px',
                  opacity: 0.9,
                  marginTop: '2px',
                  fontStyle: 'italic',
                }}
              >
                {t(`AvailabilityReason.${props.availability.reason}`)}
              </div>
            )}
          </div>
        </LevelItem>

        <LevelItem>
          <Button
            aria-label="Delete"
            variant="danger"
            onClick={() => props.removeEmployeeAvailability(props.availability)}
            isSmall
          >
            <TrashIcon />
          </Button>
        </LevelItem>
      </Level>
      <Level hasGutter>
        <LevelItem>
          <Button
            aria-label="Desired"
            title={t('EmployeeAvailabilityState.DESIRED')}
            onClick={() => props.updateEmployeeAvailability({
              ...props.availability,
              state: 'DESIRED',
            })}
            style={{
              backgroundColor: 'green',
              margin: '1px',
              width: 'min-content',
            }}
            variant="tertiary"
          >
            <OkIcon />
          </Button>
          <Button
            aria-label="Undesired"
            title={t('EmployeeAvailabilityState.UNDESIRED')}
            onClick={() => props.updateEmployeeAvailability({
              ...props.availability,
              state: 'UNDESIRED',
            })}
            style={{
              backgroundColor: 'yellow',
              margin: '1px',
              width: 'min-content',
            }}
            variant="tertiary"
          >
            <WarningTriangleIcon />
          </Button>
          <Button
            aria-label="Unavailable"
            title={t('EmployeeAvailabilityState.UNAVAILABLE')}
            onClick={() => props.updateEmployeeAvailability({
              ...props.availability,
              state: 'UNAVAILABLE',
            })}
            style={{
              backgroundColor: 'red',
              margin: '1px',
              width: 'min-content',
            }}
            variant="tertiary"
          >
            <ErrorCircleOIcon />
          </Button>
        </LevelItem>
      </Level>
    </span>
  );
};

export { AvailabilityPopoverBody, AvailabilityPopoverHeader };

export default AvailabilityEvent;
